import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { markPaymentAsExecuted } from 'lib/api';
import { executeConfirmingPayments } from 'lib/api/confirming';
import { executeCreditRequestPayouts } from 'lib/api/creditRequest';
import {
  executePaymentRequestPassivePayouts,
  executePaymentRequestPayouts,
} from 'lib/api/payments';
import NomineeExporter from 'lib/common/NomineeExporter';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { formatDate, hasAnyPermissions } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

const headers: { key: string; label: string; type?: any }[] = [
  { key: 'businessOriginName', label: 'Cuenta Origen' },
  { key: 'bankAccountOriginName', label: 'Banco Origen' },
  { key: 'businessDestinationName', label: 'Cuenta Destino' },
  { key: 'bankAccountDestinationName', label: 'Banco Destino' },
  { key: 'executedAt', label: 'Giro', type: 'date' },
  { key: 'amount', label: 'Monto', type: 'money' },
];

const PayOutDetailPayouts = (props: any) => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const {
    payments,
    source,
    sourceId,
    setSuccess,
    setFailure,
    setActiveTab,
    setUp,
    refreshPaymentGroupDetails,
  } = props;
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [nomineeExporterOpen, setNomineeExporterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const newFinancingDate = new Date();
  newFinancingDate.setUTCHours(12);
  const [executedAt, setExecutedAt] = useState(newFinancingDate);
  const dispatch = useDispatch();

  const cantSelectRow = (row: any) => {
    return row.executedAt;
  };

  const canSelectAll = () => {
    return !payments.every((payment: any) => payment.executedAt);
  };

  const executePayments = async () => {
    setLoading(true);
    try {
      if (source === 'payment-request') {
        await executePaymentRequestPayouts(dispatch, sourceId, {
          payoutsIds: selectedPayments,
          executedAt,
        });
      } else if (source === 'payment-request-passive') {
        await executePaymentRequestPassivePayouts(dispatch, sourceId, {
          payoutsIds: selectedPayments,
          executedAt,
        });
      } else if (source === 'factoring-request') {
        await markPaymentAsExecuted(dispatch, {
          id: sourceId,
          paymentsIds: selectedPayments,
          executedAt,
        });
      } else if (source === 'credit-request') {
        await executeCreditRequestPayouts(dispatch, sourceId, {
          payoutsIds: selectedPayments,
          executedAt,
        });
      } else if (source === 'confirming-request') {
        await executeConfirmingPayments(dispatch, {
          id: sourceId,
          paymentsIds: selectedPayments,
          executedAt,
        });
      }
      setOpen(false);
      setSuccess('Pagos completados correctamente');
      // Switch to Resumen tab (index 0) after successful completion
      if (setActiveTab) {
        setActiveTab(0);
      }
      // Refresh both the main payouts list and the payment group details
      if (setUp) {
        await setUp();
      }
      if (refreshPaymentGroupDetails) {
        await refreshPaymentGroupDetails(source, sourceId);
      }
    } catch (error) {
      console.error('error', error);
      setFailure('Error al completar los pagos');
    }
    setLoading(false);
  };

  const handleFinancingAtChange = (e: any) => {
    const date = new Date(e.target.value);
    date.setUTCHours(12);
    setExecutedAt(date);
  };

  const buttons: CustomButtonProps[] = [
    {
      action: () => setNomineeExporterOpen(true),
      name: 'Exportar',
      color: 'secondary',
    },
    {
      action: () => setOpen(true),
      name: 'Completar pagos',
      color: 'primary',
      disabled:
        selectedPayments.length === 0 ||
        !hasAnyPermissions(user, ['financing_payout_execute']),
    },
  ];

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <div>
      <HeaderWithActions
        title="Acá puedes ver los pagos de la nómina."
        variant="body1"
        buttons={buttons}
      />
      <EnhancedTable
        dataHeaders={headers}
        data={payments}
        selected={selectedPayments}
        setSelected={setSelectedPayments}
        canSelectAll={canSelectAll()}
        cantSelectRow={cantSelectRow}
        finalSumRow
      />
      <NomineeExporter
        open={nomineeExporterOpen}
        setOpen={setNomineeExporterOpen}
        payments={payments}
        id={sourceId}
      />
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Completar pagos</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro que deseas completar los pagos seleccionados?
          </Typography>
          <Typography>
            Estos serán completados con la fecha de financiamiento:
          </Typography>
          <div style={{ marginTop: 10, textAlign: 'center' }}>
            <TextField
              value={formatDate(executedAt)}
              onChange={handleFinancingAtChange}
              type="date"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <CustomButton color="secondary" onClick={() => setOpen(false)}>
            Cancelar
          </CustomButton>
          <CustomButton color="primary" onClick={executePayments}>
            Confirmar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PayOutDetailPayouts;
